import { Alert, Button, Card, CardBody, Input } from '@foundation/ui'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import styles from './onboarding.module.css'
import { AppLayout } from '@/components/AppLayout'
import { api } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'

export const Route = createFileRoute('/onboarding')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: OnboardingPage,
})

function OnboardingPage() {
  const navigate = useNavigate()
  const [url, setUrl] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedUrl = url.trim()
    if (!trimmedUrl) {
      setError('Please enter a URL to track.')
      return
    }

    try {
      setIsSubmitting(true)
      setError(null)

      const payload: { url: string; displayName?: string } = { url: trimmedUrl }
      const trimmedName = displayName.trim()
      if (trimmedName) {
        payload.displayName = trimmedName
      }

      const newUrl = await api.createTrackedUrl(payload)
      void navigate({ to: '/urls/$id', params: { id: newUrl.id } })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add URL. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSkip = () => {
    void navigate({ to: '/dashboard' })
  }

  return (
    <AppLayout title="Getting Started">
      <div className={styles.onboarding}>
        <Card className={styles.onboardingCard}>
          <CardBody>
            <h2 className={styles.onboardingTitle}>Welcome to PriceSpy</h2>
            <p className={styles.onboardingDescription}>
              Add your first competitor pricing page to start monitoring
            </p>

            {error && (
              <Alert variant="danger" style={{ marginBottom: '1rem' }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="url">URL to track</label>
                <Input
                  id="url"
                  type="url"
                  placeholder="https://competitor.com/pricing"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="displayName">Display name (optional)</label>
                <Input
                  id="displayName"
                  type="text"
                  placeholder="e.g. Competitor Pricing"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.formActions}>
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Adding...' : 'Start Tracking'}
                </Button>
                <button
                  type="button"
                  className={styles.skipLink}
                  onClick={handleSkip}
                  disabled={isSubmitting}
                >
                  Skip for now
                </button>
              </div>
            </form>
          </CardBody>
        </Card>
      </div>
    </AppLayout>
  )
}
